import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { supabaseV2 } from "../lib/supabase";

export type UnlinkedPlayerOption = {
  player_id: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string;
  ea_account_handle: string | null;
  role_code: string | null;
  jersey_number: number | null;
  is_guest: boolean;
};

function normalize(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

/**
 * Step "Sei tu?": lista dei giocatori non ancora collegati del team, da cui
 * l'utente sceglie la propria card. Crea una richiesta in player_link_requests
 * che il founder dovra' approvare — nessun aggancio automatico.
 *
 * Condiviso tra JoinTeamPage (subito dopo consume_team_invite) e PlayerArea
 * (UnlinkedPlayerScreen, quando l'utente e' gia' membro ma non collegato).
 */
export function PickYourselfStep({
  teamId,
  teamName,
  firstNameHint,
  lastNameHint,
  hideHeader = false,
  onGoToApp,
}: {
  teamId: string;
  teamName: string;
  firstNameHint: string;
  lastNameHint: string;
  hideHeader?: boolean;
  onGoToApp?: () => void;
}) {
  const { t } = useTranslation("public");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestSent, setRequestSent] = useState(false);

  // Se l'utente ha gia' una richiesta pending per questo team, partiamo
  // direttamente dallo stato "inviata" (la RLS filtra alle proprie richieste).
  const pendingQuery = useQuery({
    queryKey: ["my-pending-player-link", teamId],
    queryFn: async () => {
      const { data, error: qErr } = await supabaseV2
        .from("player_link_requests")
        .select("id")
        .eq("team_id", teamId)
        .eq("status", "pending")
        .limit(1);
      if (qErr) throw qErr;
      return (data || []).length > 0;
    },
    retry: false,
  });

  const playersQuery = useQuery({
    queryKey: ["unlinked-players-for-link", teamId],
    queryFn: async () => {
      const { data, error: rpcErr } = await supabaseV2.rpc("list_unlinked_players_for_link", {
        _team_id: teamId,
      });
      if (rpcErr) throw rpcErr;
      return (data || []) as UnlinkedPlayerOption[];
    },
    retry: false,
  });

  const players = playersQuery.data || [];

  const hintFirst = normalize(firstNameHint);
  const hintLast = normalize(lastNameHint);

  const matchScore = (p: UnlinkedPlayerOption): number => {
    if (!hintFirst && !hintLast) return 0;
    const f = normalize(p.first_name);
    const l = normalize(p.last_name);
    let score = 0;
    if (f && hintFirst && (f === hintFirst || f.startsWith(hintFirst) || hintFirst.startsWith(f))) {
      score += 2;
    }
    if (l && hintLast && (l === hintLast || l.startsWith(hintLast) || hintLast.startsWith(l))) {
      score += 2;
    }
    return score;
  };

  const orderedPlayers = useMemo(() => {
    const withScore = players.map((p) => ({ p, score: matchScore(p) }));
    withScore.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (a.p.display_name || "").localeCompare(b.p.display_name || "");
    });
    return withScore;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, hintFirst, hintLast]);

  const submit = async (playerId: string) => {
    setSubmitting(true);
    setError(null);
    const { error: rpcErr } = await supabaseV2.rpc("request_player_link", {
      _team_id: teamId,
      _player_id: playerId,
      _note: null,
    });
    setSubmitting(false);
    if (rpcErr) {
      setError(rpcErr.message || String(rpcErr));
      return;
    }
    setRequestSent(true);
  };

  if (pendingQuery.isLoading || playersQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("join.pickYourself.loading")}
      </div>
    );
  }

  if (requestSent || pendingQuery.data === true) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
          <p className="font-semibold">{t("join.pickYourself.sentTitle")}</p>
          <p className="text-xs mt-1">{t("join.pickYourself.sentBody")}</p>
        </div>
        {onGoToApp && (
          <Button variant="outline" className="w-full" onClick={onGoToApp}>
            {t("join.pickYourself.goToApp")}
          </Button>
        )}
      </div>
    );
  }

  // Nessun profilo da reclamare: l'utente non si auto-aggiunge. E' gia' membro,
  // il founder e' stato avvisato (vedi consume_team_invite) e gli aprira' uno
  // spazio in rosa. Schermata "in attesa", nessuna azione possibile.
  if (players.length === 0) {
    return (
      <div className="rounded-xl bg-sky-50 px-3 py-3 text-sm text-sky-800">
        <p className="font-semibold">{t("join.pickYourself.waitingTitle")}</p>
        <p className="text-xs mt-1">
          <Trans
            i18nKey="join.pickYourself.waitingBody"
            t={t}
            values={{ teamName }}
            components={{ strong: <strong /> }}
          />
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {!hideHeader && (
        <div>
          <p className="text-sm font-semibold">{t("join.pickYourself.title")}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {t("join.pickYourself.subtitle", { teamName })}
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[44vh] overflow-y-auto pr-1">
        {orderedPlayers.map(({ p, score }, idx) => {
          const isTopMatch = idx === 0 && score >= 4;
          const primary =
            (p.display_name || `${p.first_name || ""} ${p.last_name || ""}`).trim() ||
            p.ea_account_handle ||
            "—";
          const secondary = [p.role_code, p.jersey_number ? `#${p.jersey_number}` : null]
            .filter(Boolean)
            .join(" · ");
          return (
            <button
              key={p.player_id}
              type="button"
              disabled={submitting}
              onClick={() => submit(p.player_id)}
              className={
                "text-left rounded-2xl bg-background px-3 py-2.5 hover:bg-raised transition-colors disabled:opacity-60 " +
                (isTopMatch ? "ring-1 ring-accent" : "")
              }
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold leading-tight truncate flex-1">{primary}</span>
                {p.is_guest && (
                  <span className="text-[11px] bg-amber-500/15 text-amber-700 font-semibold px-1.5 py-0.5 rounded shrink-0">
                    {t("join.pickYourself.guestTag")}
                  </span>
                )}
                {isTopMatch && (
                  <span className="text-[11px] bg-sky-50 text-accent font-semibold px-1.5 py-0.5 rounded shrink-0">
                    {t("join.pickYourself.likelyYou")}
                  </span>
                )}
              </div>
              {secondary && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{secondary}</p>
              )}
            </button>
          );
        })}
      </div>

      {/* Non ti trovi? Nessuna azione: il founder e' gia' stato avvisato del
          tuo ingresso (consume_team_invite) e ti aggiungera' lui. */}
      <p className="text-xs text-muted-foreground text-center leading-snug pt-1">
        {t("join.pickYourself.notInListInfo")}
      </p>
    </div>
  );
}
